import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Truck, Boxes } from "lucide-react";
import { Button } from "@/components/ui/button";
import SearchBar from "@/components/SearchBar";

const EXAMPLES = ["Daikin air handlers", "Goodman furnaces", "Midea heat pump", "Coils"];

const TRUST = [
  { icon: Boxes, label: "Major HVAC Brands", tone: "cool" },
  { icon: ShieldCheck, label: "Manufacturer Warranty", tone: "cool" },
  { icon: Truck, label: "Local Delivery", tone: "cool" },
];

export default function Hero() {
  return (
    <section className="relative z-10 bg-[#0B1528]">
      <span className="thermo-bar absolute inset-x-0 top-0 h-1" />
      <div className="tech-dots pointer-events-none absolute inset-0 opacity-50" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.09]"
        style={{ backgroundImage: "radial-gradient(circle at 18% 20%, #38bdf8 0, transparent 42%), radial-gradient(circle at 88% 30%, #f97316 0, transparent 38%)" }}
      />
      <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14 lg:py-24 lg:px-8">
        <div className="flex flex-col justify-center">
          <span className="inline-flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-sky-400">
            <span className="h-1.5 w-1.5 rounded-full bg-sky-400" /> Commercial &amp; Residential Distribution
          </span>
          <h1 className="mt-5 font-heading text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            Industrial-Grade HVAC<br />Equipment, <span className="text-sky-400">Direct.</span>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            Condensers, heat pumps, packaged units, ductless mini-splits and VRF systems from established
            manufacturers — available to homeowners and trade. Browse by category, brand or model number.
          </p>
          <div className="relative z-20 mt-7 lg:hidden"><SearchBar testId="hero-search-mobile" /></div>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg" className="rounded-sm bg-sky-600 px-8 font-semibold text-white hover:bg-sky-500" data-testid="hero-shop-equipment">
              <Link to="/shop">Explore Equipment Catalog <ArrowRight className="h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-sm border-slate-600 bg-transparent px-8 font-semibold text-white hover:bg-white/10 hover:text-white" data-testid="hero-shop-category">
              <a href="#shop-by-category">Browse by Department</a>
            </Button>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-3 text-sm text-slate-300 sm:flex sm:flex-wrap">
            {TRUST.map((t) => {
              const Icon = t.icon;
              return (
                <span key={t.label} className="inline-flex items-center gap-2">
                  <Icon className={`h-4 w-4 ${t.tone === "warm" ? "text-orange-400" : "text-sky-400"}`} />
                  {t.label}
                </span>
              );
            })}
          </div>
        </div>

        <div className="relative z-20 hidden flex-col justify-center lg:flex" data-testid="hero-search-panel">
          <span className="font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-slate-300">Find your equipment</span>
          <p className="mt-2 font-heading text-xl font-bold text-white sm:text-2xl">Search the full catalog</p>
          <div className="mt-4"><SearchBar testId="hero-search" /></div>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
            <span className="font-mono uppercase tracking-wider text-slate-400">Try:</span>
            {EXAMPLES.map((ex) => (
              <Link key={ex} to={`/shop?q=${encodeURIComponent(ex)}`} className="rounded-sm border border-slate-600 px-2.5 py-1 text-slate-200 transition-colors duration-150 hover:border-sky-400 hover:text-white" data-testid={`hero-search-example-${ex.toLowerCase().replace(/\s+/g, "-")}`}>
                {ex}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
